import React, { useEffect, useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';

interface FirstTimeUserGuideProps {
  onDismiss: () => void;
  targetElementId?: string;
}

/**
 * First-Time User Guide Overlay
 * Highlights the "Record Swing" button and shows welcome message
 */
export function FirstTimeUserGuide({ onDismiss, targetElementId = 'record-swing-button' }: FirstTimeUserGuideProps) {
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    // Find the target element (Record Swing button)
    const findTarget = () => {
      // Try multiple strategies to find the button
      let target: HTMLElement | null = null;

      // Strategy 1: Find by data attribute
      target = document.querySelector(`[data-guide-target="${targetElementId}"]`) as HTMLElement;

      // Strategy 2: Find by text content (fallback)
      if (!target) {
        const buttons = Array.from(document.querySelectorAll('button'));
        target = buttons.find(btn => 
          btn.textContent?.includes('Record Swing') || 
          btn.textContent?.includes('Record')
        ) as HTMLElement || null;
      }

      // Strategy 3: Find in QuickActionsTile (first button in grid)
      if (!target) {
        const quickActionsTile = document.querySelector('[data-figma-id="quick-actions-tile"]');
        if (quickActionsTile) {
          const firstButton = quickActionsTile.querySelector('button');
          if (firstButton) {
            target = firstButton as HTMLElement;
          }
        }
      }

      if (target) {
        const rect = target.getBoundingClientRect();
        setTargetRect(rect);
        setShowModal(true);
      }
    };

    // Wait for DOM to be ready
    const timeout = setTimeout(findTarget, 100);

    // Update position on scroll/resize
    const updatePosition = () => {
      findTarget();
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [targetElementId]);

  if (!targetRect || !showModal) {
    return null;
  }

  // Calculate highlight box position with padding
  const padding = 8;
  const highlightStyle: React.CSSProperties = {
    position: 'fixed',
    left: `${targetRect.left - padding}px`,
    top: `${targetRect.top - padding}px`,
    width: `${targetRect.width + padding * 2}px`,
    height: `${targetRect.height + padding * 2}px`,
    borderRadius: '12px',
    border: '4px solid rgb(34, 197, 94)', // lime-500
    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.65), 0 0 20px rgba(34, 197, 94, 0.5)',
    pointerEvents: 'none',
    zIndex: 9998,
    transition: 'all 0.2s ease-out',
  };

  // Position modal below or above the highlight (responsive)
  const isMobile = window.innerWidth < 640;
  const modalTop = isMobile 
    ? `${targetRect.bottom + 20}px`
    : `${targetRect.top - 200}px`;
  const modalLeft = isMobile ? '50%' : `${targetRect.left}px`;
  const modalTransform = isMobile ? 'translateX(-50%)' : 'none';
  const modalWidth = isMobile ? '90%' : '400px';
  const maxWidth = isMobile ? '90%' : '400px';

  const modalStyle: React.CSSProperties = {
    position: 'fixed',
    top: modalTop,
    left: modalLeft,
    transform: modalTransform,
    width: modalWidth,
    maxWidth: maxWidth,
    zIndex: 9999,
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/65 z-[9997]"
        onClick={onDismiss}
        aria-hidden="true"
      />

      {/* Highlight Box */}
      <div
        ref={highlightRef}
        style={highlightStyle}
        aria-hidden="true"
      />

      {/* Modal Card */}
      <div
        style={modalStyle}
        className="bg-white dark:bg-neutral-900 rounded-xl shadow-xl p-6 animate-in fade-in-0 zoom-in-95 duration-200"
        role="dialog"
        aria-modal="true"
        aria-labelledby="guide-title"
      >
        <div className="flex items-start justify-between mb-4">
          <h2 id="guide-title" className="text-xl font-bold text-slate-900 dark:text-white">
            👋 Welcome to No Doubles!
          </h2>
          <button
            onClick={onDismiss}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
            aria-label="Close guide"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
          Start here: Record your swing.
        </p>

        <ul className="text-sm text-slate-600 dark:text-slate-300 mb-6 space-y-2 list-disc list-inside">
          <li>Tap "Record Swing" to begin</li>
          <li>Upload or record a swing video</li>
          <li>Get instant AI analysis</li>
        </ul>

        <div className="flex gap-3 justify-end">
          <Button
            variant="outline"
            onClick={onDismiss}
            className="px-4 py-2"
          >
            Skip
          </Button>
          <Button
            onClick={onDismiss}
            className="px-4 py-2 bg-lime-500 hover:bg-lime-600 text-black"
          >
            Got It
          </Button>
        </div>
      </div>
    </>
  );
}
